import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Clock3, MapPin, Plane, Search, Users } from "lucide-react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO from "../SEO/SEO";
import { get } from "../../utils/request";
import "./airport-lounges.scss";

const loungeImage = (lounge) =>
  lounge?.mediaAssets?.[0]?.url ||
  lounge?.providerImageUrl ||
  "/images/editorial/mountain-hero.webp";

export default function AirportLoungesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCode = String(searchParams.get("country") || "").toUpperCase();
  const selectedNameFromUrl = searchParams.get("countryName") || "";
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(selectedCode);
  const [lounges, setLounges] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingLounges, setLoadingLounges] = useState(Boolean(selectedCode));
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    get("/lounge/countries")
      .then((result) => {
        if (!active) return;
        const nextCountries = Array.isArray(result) ? result : [];
        setCountries(nextCountries);
        if (!country && nextCountries[0]?.countryCode) setCountry(nextCountries[0].countryCode);
      })
      .catch(() => active && setError("Lounge countries are temporarily unavailable."))
      .finally(() => active && setLoadingCountries(false));
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedCode) {
      setLounges([]);
      setLoadingLounges(false);
      return undefined;
    }
    let active = true;
    setLoadingLounges(true);
    setError("");
    get("/lounge/list", { country: selectedCode })
      .then((result) => active && setLounges(Array.isArray(result) ? result : []))
      .catch(() => active && setError("We could not load lounges for this country."))
      .finally(() => active && setLoadingLounges(false));
    return () => { active = false; };
  }, [selectedCode]);

  const selectedCountry = useMemo(
    () => countries.find((item) => item.countryCode === selectedCode),
    [countries, selectedCode],
  );
  const countryName = selectedCountry?.countryName || selectedNameFromUrl;

  const submitSearch = (event) => {
    event.preventDefault();
    if (!country) return;
    const match = countries.find((item) => item.countryCode === country);
    const params = new URLSearchParams({ country });
    if (match?.countryName) params.set("countryName", match.countryName);
    navigate(`/airport-lounges/search?${params.toString()}`);
  };

  return (
    <>
      <SEO
        title="Airport Lounges | Teleport Now"
        description="Search currently available airport lounges by country and review live locations and prices."
        canonicalPath="/airport-lounges"
      />
      <Navbar />
      <main className="lounge-page">
        <header className="lounge-hero">
          <div className="lounge-hero__content">
            <span className="lounge-hero__icon"><Plane aria-hidden="true" /></span>
            <p>Airport Lounge</p>
            <h1>Find a calmer place before takeoff.</h1>
            <form className="lounge-search" onSubmit={submitSearch}>
              <label htmlFor="lounge-country"><MapPin aria-hidden="true" /> Country</label>
              <select
                id="lounge-country"
                value={country}
                onChange={(event) => setCountry(event.target.value)}
                disabled={loadingCountries}
              >
                <option value="">{loadingCountries ? "Loading countries…" : "Select a country"}</option>
                {countries.map((item) => (
                  <option key={item.countryCode} value={item.countryCode}>
                    {item.countryName}
                  </option>
                ))}
              </select>
              <button type="submit" disabled={!country || loadingCountries}>
                <Search aria-hidden="true" /> Search lounges
              </button>
            </form>
          </div>
        </header>

        <section className="lounge-results" aria-live="polite">
          {selectedCode ? (
            <div className="lounge-results__heading">
              <div>
                <p>Available lounges</p>
                <h2>{countryName || selectedCode}</h2>
              </div>
              {!loadingLounges && <span>{lounges.length} {lounges.length === 1 ? "lounge" : "lounges"}</span>}
            </div>
          ) : (
            <div className="lounge-results__intro">
              <h2>Search by country</h2>
              <p>Select where you are travelling to see supported airports, terminals and current lounge prices.</p>
            </div>
          )}

          {error && <div className="lounge-status lounge-status--error">{error}</div>}
          {loadingLounges && <div className="lounge-status">Loading live lounge options…</div>}
          {!loadingLounges && selectedCode && !error && lounges.length === 0 && (
            <div className="lounge-status">No active lounges are currently available for this country.</div>
          )}

          {!loadingLounges && lounges.length > 0 && (
            <div className="lounge-grid">
              {lounges.map((lounge) => (
                <article className="lounge-card" key={lounge._id}>
                  <img src={loungeImage(lounge)} alt="" loading="lazy" />
                  <div className="lounge-card__body">
                    <p>{lounge.airportIata || lounge.city}</p>
                    <h3>{lounge.loungeName}</h3>
                    <div className="lounge-card__location">
                      <MapPin aria-hidden="true" />
                      <span>{[lounge.airport, lounge.terminal, lounge.hall].filter(Boolean).join(" · ")}</span>
                    </div>
                    <div className="lounge-card__meta">
                      {lounge.durationMinutes && <span><Clock3 aria-hidden="true" /> Up to {lounge.durationMinutes} min</span>}
                      {lounge.providerAudience && <span><Users aria-hidden="true" /> {lounge.providerAudience === "ALL" ? "All travellers" : lounge.providerAudience}</span>}
                    </div>
                    <div className="lounge-card__footer">
                      <strong>{lounge.pricing?.displayFromPriceLabel || "Check current price"}</strong>
                      <Link to={`/airport-lounges/${lounge._id}`}>View lounge</Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}
